import { useCallback, type MouseEvent, type ReactNode } from 'react';
import {
  Badge,
  Checkbox,
  Datatable,
  DatatableBody,
  DatatableCell,
  DatatableHead,
  DatatableHeader,
  DatatableRow,
  Skeleton,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@monksflow/monks-ui';
import { OwnerCell } from '@/shared/ui';
import { SortableHead } from '@/shared/ui/data-table';
import { EmptyState } from '@/shared/ui/EmptyState';
import { microInteraction } from '@/shared/ui/MicroInteractions';
import { useCurrentUserId } from '@/entities/user';
import { formatRelativeDate } from '@/shared/utils/dateUtils';
import { buildFileTypeCell } from '@/features/knowledge-bases/lib';
import type {
  FileRow,
  KbDocumentSelection,
  SortableFileRowKeys,
} from '@/features/knowledge-bases/model';
import { KnowledgeBaseDocumentsTableProps } from './KnowledgeBaseDocumentsTable.types';
import { getStatusBadgeVariant, getStatusLabel } from './KnowledgeBaseDocumentsTable.constants';
import { KnowledgeBaseDocumentRowActions } from './KnowledgeBaseDocumentRowActions';
import { FileTypeCell } from './FileTypeCell';
import { useTranslation } from 'react-i18next';

const SKELETON_ROWS = 5;

// Checkbox styling lifted from the design prototype, which re-points `--primary`
// at neutral-900 for this surface. We keep our own (blue) `--primary` intact and
// name the neutral explicitly via `--background-emphasis`, which resolves to the
// same neutral-900. Empty state is a `--border-strong` hairline over `--input`,
// per the prototype's `.tsr-dt__check` rule.
const CHECKBOX_CLASSES = [
  microInteraction.checkbox,
  'border-border-strong bg-input',
  'data-[state=checked]:border-background-emphasis data-[state=checked]:bg-background-emphasis',
  'data-[state=indeterminate]:border-background-emphasis data-[state=indeterminate]:bg-background-emphasis',
].join(' ');

// Row tints, also from the prototype: hover paints `--surface`, selection paints
// one step darker, and selection outranks hover so a selected row doesn't
// lighten when the pointer crosses it.
//
// The prototype draws no ring on rows at all. The DS row base carries
// `focus-within:ring-2`, and `focus-within` (unlike `focus-visible`) also fires
// for mouse input, so clicking a row's checkbox haloed the entire row. The
// checkbox keeps its own `focus-visible` ring, so keyboard focus stays visible.
const ROW_CLASSES = 'cursor-pointer focus-within:ring-0 focus-within:ring-offset-0';
// The prototype's data row is 72px tall. Gated by `fileTypeColumnVariant` (Amendment A1):
// the flag-off, `tags`-variant table must keep rendering at today's row height.
const TYPE_VARIANT_ROW_HEIGHT_CLASS = 'h-[72px]';
// The selection tint needs a dark step of its own: `--muted` is the same stone-800
// as `--card` there, so the light-mode value paints the row its own colour and the
// state never shows. stone-600 also keeps selection ahead of the hover tint, which
// a global rule paints one step lower.
const ROW_SELECTED_CLASSES =
  '[&>*]:bg-muted hover:[&>*]:bg-muted dark:[&>*]:bg-border dark:hover:[&>*]:bg-border';

// Sortable header columns, in render order. Drives both the header cells and the body
// cells: both are mapped from the same array, so they can't drift. `fileTypeColumnVariant`
// selects which of the two orderings below is active; `SORTABLE_KEYS` (the `handleSort`
// allow-list) is order-independent, so one list covers both.
type ColumnConfig = {
  column: SortableFileRowKeys;
  labelKey: string;
  className: string | undefined;
};

// Flag-off (default): what ships today. Order and labels must never change here.
const TAGS_COLUMNS = [
  {
    column: 'fileName',
    labelKey: 'knowledgeBases.knowledgeBaseDocumentsTable.name',
    className: undefined,
  },
  {
    column: 'created',
    labelKey: 'knowledgeBases.knowledgeBaseDocumentsTable.addedAt',
    className: 'w-[180px]',
  },
  {
    column: 'createdByUsername',
    labelKey: 'knowledgeBases.knowledgeBaseDocumentsTable.addedBy',
    className: 'w-[200px]',
  },
  {
    column: 'tags',
    labelKey: 'knowledgeBases.knowledgeBaseDocumentsTable.tags',
    className: 'w-[220px]',
  },
] as const satisfies readonly ColumnConfig[];

// Flag-on: prototype order — Name, Type, Uploaded at, Uploaded by.
const TYPE_COLUMNS = [
  {
    column: 'fileName',
    labelKey: 'knowledgeBases.knowledgeBaseDocumentsTable.name',
    className: undefined,
  },
  {
    column: 'tags',
    labelKey: 'knowledgeBases.knowledgeBaseDocumentsTable.type',
    className: 'w-[240px]',
  },
  {
    column: 'created',
    labelKey: 'knowledgeBases.knowledgeBaseDocumentsTable.uploadedAt',
    className: 'w-[220px]',
  },
  {
    column: 'createdByUsername',
    labelKey: 'knowledgeBases.knowledgeBaseDocumentsTable.uploadedBy',
    className: 'w-[220px]',
  },
] as const satisfies readonly ColumnConfig[];

const SORTABLE_KEYS: SortableFileRowKeys[] = TAGS_COLUMNS.map((c) => c.column);

interface SelectionCellProps {
  row: FileRow;
  index: number;
  selection: KbDocumentSelection | undefined;
}

// Module-scope: used inside `rows.map`, closes over only `selection`.
function SelectionCell({ row, index, selection }: SelectionCellProps) {
  const { t } = useTranslation('knowledge');

  if (!selection) return null;
  const selectable = selection.isSelectable(row);
  // Explain the disabled checkbox for the "source missing" case, mirroring the
  // row Download action; an in-flight upload is self-evidently not ready yet.
  const disabledReason =
    !row.assetId && !row.isUploading
      ? t('knowledgeBases.knowledgeBaseDocumentsTable.theSourceForThisFile')
      : '';

  const checkbox = (
    <Checkbox
      aria-label={t('knowledgeBases.knowledgeBaseDocumentsTable.selectDocument', {
        name: row.fileName || t('knowledgeBases.knowledgeBaseDocumentsTable.document'),
      })}
      checked={selection.isRowSelected(row)}
      disabled={!selectable}
      className={CHECKBOX_CLASSES}
      // Own the toggle here so keyboard users can select: activating a focused
      // checkbox with Space/Enter dispatches a click (shiftKey=false) that this
      // handler catches. stopPropagation keeps the enclosing cell/row handlers
      // from double-toggling or opening the detail view.
      onClick={(event) => {
        event.stopPropagation();
        if (!selectable) return;
        selection.onToggleRow(index, event);
      }}
    />
  );

  return (
    <DatatableCell
      className="w-[44px] pr-0"
      onMouseDown={(event) => {
        // Suppress native shift-click text highlight so range-select reads as
        // a click interaction.
        if (event.shiftKey) event.preventDefault();
      }}
      onClick={(event) => {
        // Keep the row's own onClick (view detail) from also firing.
        event.stopPropagation();
        if (!selectable) return;
        selection.onToggleRow(index, event);
      }}
    >
      {disabledReason ? (
        <TooltipProvider delayDuration={300}>
          <Tooltip>
            <TooltipTrigger asChild>
              {/* Wrapper span keeps the tooltip reachable while disabled. */}
              <span className="inline-flex">{checkbox}</span>
            </TooltipTrigger>
            <TooltipContent>{disabledReason}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ) : (
        checkbox
      )}
    </DatatableCell>
  );
}

export function KnowledgeBaseDocumentsTable({
  rows,
  onViewDetail,
  onDownload,
  onDelete,
  emptyTitle,
  emptyDescription,
  isLoading = false,
  sortBy = 'created',
  sortDirection = 'desc',
  onSort,
  canModify,
  selection,
  fileTypeColumnVariant = 'tags',
}: KnowledgeBaseDocumentsTableProps) {
  const { t } = useTranslation('knowledge');

  const currentUserId = useCurrentUserId();
  const hasSelection = Boolean(selection);
  const columns = fileTypeColumnVariant === 'type' ? TYPE_COLUMNS : TAGS_COLUMNS;
  // Checkbox (optional) + Name + Added At/Type + Added By/Uploaded at + Tags/Uploaded by + Actions.
  const columnCount = (hasSelection ? 1 : 0) + 5;

  const handleSort = useCallback(
    (property: SortableFileRowKeys) => {
      if (!SORTABLE_KEYS.includes(property)) return;
      // eslint-disable-next-line i18next/no-literal-string -- sort direction state values
      const newDirection = sortBy === property && sortDirection === 'asc' ? 'desc' : 'asc';
      onSort(property, newDirection);
    },
    [onSort, sortBy, sortDirection],
  );

  const headerCheckedState: boolean | 'indeterminate' = selection?.allPageSelected
    ? true
    : selection?.somePageSelected
      ? 'indeterminate'
      : false;

  const tableIsEmpty = !isLoading && rows.length === 0;
  if (tableIsEmpty) {
    return (
      <div className="flex h-full flex-col justify-center gap-4 px-8 py-2">
        <EmptyState
          title={emptyTitle ?? t('knowledgeBases.knowledgeBaseDocumentsTable.noDocumentsAddedYet')}
          description={
            emptyDescription ??
            t('knowledgeBases.knowledgeBaseDocumentsTable.clickOnAddDocumentsTo')
          }
          height="400px"
        />
      </div>
    );
  }

  return (
    // The `tags`-variant page renders this table unwrapped, so it carries its own 24px
    // horizontal inset (px-6). The `type`-variant page wraps it in a 24px-padded card
    // instead (Amendment A1): keeping px-6 there would double the inset to 48px, so it's
    // gated off for that arm, the same discriminator that already selects the column set.
    //
    <Datatable
      className={`table-fixed **:data-[slot=datatable-cell]:py-2.5 **:data-[slot=datatable-cell]:text-sm **:data-[slot=datatable-cell]:font-normal **:data-[slot=datatable-cell]:text-foreground **:data-[slot=datatable-cell]:border-b **:data-[slot=datatable-cell]:border-border-light **:data-[slot=datatable-head]:text-sm **:data-[slot=datatable-head]:font-normal **:data-[slot=datatable-head]:text-muted-foreground${fileTypeColumnVariant === 'type' ? '' : ' px-6'}`}
    >
      <DatatableHeader>
        <DatatableRow>
          {selection && (
            <DatatableHead className="w-[44px] pr-0">
              <Checkbox
                aria-label={t(
                  'knowledgeBases.knowledgeBaseDocumentsTable.selectAllDocumentsOnThis',
                )}
                checked={headerCheckedState}
                disabled={selection.pageSelectableCount === 0}
                onCheckedChange={selection.onToggleSelectAllPage}
                className={CHECKBOX_CLASSES}
              />
            </DatatableHead>
          )}
          {columns.map(({ column, labelKey, className }) => (
            <SortableHead
              key={column}
              column={column}
              label={t(labelKey)}
              className={className}
              sortBy={sortBy}
              sortDirection={sortDirection}
              onSort={handleSort}
            />
          ))}
          <DatatableHead className="w-[56px]">
            <span className="sr-only">
              {t('knowledgeBases.knowledgeBaseDocumentsTable.actions')}
            </span>
          </DatatableHead>
        </DatatableRow>
      </DatatableHeader>
      <DatatableBody>
        {/* single call site — inlined instead of a `renderSkeleton` closure. */}
        {isLoading &&
          Array.from({ length: SKELETON_ROWS }).map((_, index) => (
            <DatatableRow key={`skeleton-${index}`}>
              {Array.from({ length: columnCount }).map((_, colIndex) => (
                <DatatableCell key={`skeleton-cell-${colIndex}`}>
                  <Skeleton className="h-4 w-3/4" />
                </DatatableCell>
              ))}
            </DatatableRow>
          ))}
        {!isLoading &&
          rows.map((row, index) => {
            const isSelected = selection?.isRowSelected(row) ?? false;
            const handleRowClick = (event: MouseEvent) => {
              // Shift-click is a range-select gesture, not a "view" gesture, when
              // selection is active — swallow it so it doesn't open the detail.
              if (selection && event.shiftKey) return;
              onViewDetail(row);
            };
            const cellsByColumn: Record<SortableFileRowKeys, ReactNode> = {
              fileName: (
                <div className="flex items-center gap-2">
                  <span className="truncate font-medium">
                    {row.fileName || t('knowledgeBases.knowledgeBaseDocumentsTable.untitled')}
                  </span>
                  {row.isUploading && row.uploadStatus && (
                    <Badge
                      variant={getStatusBadgeVariant(row.uploadStatus)}
                      roundness="full"
                      className={microInteraction.statusBadge}
                    >
                      {getStatusLabel(row.uploadStatus)}
                    </Badge>
                  )}
                </div>
              ),
              created: row.created ? formatRelativeDate(row.created) : '--',
              createdByUsername: (
                <OwnerCell
                  owner={{
                    name:
                      row.createdByUsername ||
                      t('knowledgeBases.knowledgeBaseDocumentsTable.unknown'),
                    avatarUrl: null,
                    isMe: !!row.createdBy && row.createdBy === currentUserId,
                  }}
                />
              ),
              tags:
                fileTypeColumnVariant === 'type' ? (
                  <FileTypeCell model={buildFileTypeCell(row.tags)} />
                ) : Array.isArray(row.tags) && row.tags.length > 0 ? (
                  <div className="flex flex-wrap gap-1">
                    {row.tags.map((tag) => (
                      <Badge key={tag} variant="secondary" roundness="full">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                ) : (
                  '--'
                ),
            };
            return (
              <DatatableRow
                key={row.id}
                interactive
                state={isSelected ? 'selected' : 'default'}
                className={`${ROW_CLASSES} ${isSelected ? ROW_SELECTED_CLASSES : ''} ${fileTypeColumnVariant === 'type' ? TYPE_VARIANT_ROW_HEIGHT_CLASS : ''}`}
                onMouseDown={(event) => {
                  if (selection && event.shiftKey) event.preventDefault();
                }}
                onClick={handleRowClick}
              >
                <SelectionCell row={row} index={index} selection={selection} />
                {columns.map(({ column }) => (
                  <DatatableCell key={column}>{cellsByColumn[column]}</DatatableCell>
                ))}
                <DatatableCell>
                  <KnowledgeBaseDocumentRowActions
                    row={row}
                    onViewDetail={onViewDetail}
                    onDownload={onDownload}
                    onDelete={onDelete}
                    canModify={canModify}
                  />
                </DatatableCell>
              </DatatableRow>
            );
          })}
      </DatatableBody>
    </Datatable>
  );
}

KnowledgeBaseDocumentsTable.displayName = 'KnowledgeBaseDocumentsTable';
